import type { TrackedTranslation } from '@ps/web-core-intl';
import type { PokerMttQueryVariables } from '@ps/web-martech-cms-types';
import { camelCase } from 'lodash';

import type { TournamentsQueryVariables } from '@swf/generated/graphql/poker/MTT/operations';

import type {
  Filter,
  FilterNameType,
  FilterOptionBase,
  FilterOptionGroup,
  FilterOptionGroupLabelMap,
  FilterOptionLabelMap,
  FilterTypes,
  FiltersConfig,
  RawFilter,
  RawSection
} from '../../types/MTT';
import {
  FilterName,
  FilterOptionBuyIn,
  FilterOptionGame,
  FilterOptionSpeed,
  FilterOptionTableSize,
  FilterSectionNameGame
} from '../../types/MTT';
import { criteriaMatches, parseHSPCriteria } from '../HSPCriteria';

import {
  TXTCLI_2_7_Single_Draw,
  TXTCLI_2_7_Triple_Draw,
  TXTCLI_2_Players,
  TXTCLI_3_6_Players,
  TXTCLI_5_Card_Draw,
  TXTCLI_5_Card_Omaha,
  TXTCLI_6X_HoldXem,
  TXTCLI_7_10_Players,
  TXTCLI_8_Game,
  TXTCLI_Badugi,
  TXTCLI_Buy_In,
  TXTCLI_Courchevel,
  TXTCLI_Custom_Range,
  TXTCLI_Draw_Games,
  TXTCLI_Fusion,
  TXTCLI_Game,
  TXTCLI_HORSE,
  TXTCLI_HOSE,
  TXTCLI_High,
  TXTCLI_HoldXemXOmaha,
  TXTCLI_HoldXem_Games,
  TXTCLI_Hyper_Turbo,
  TXTCLI_Limit_HoldXem,
  TXTCLI_Low,
  TXTCLI_Medium,
  TXTCLI_Micro,
  TXTCLI_Mixed_Games,
  TXTCLI_Mixed_HoldXem,
  TXTCLI_Mixed_Omaha,
  TXTCLI_No_Limit_HoldXem,
  TXTCLI_No_Limit_Omaha,
  TXTCLI_Omaha_Games,
  TXTCLI_Omaha_Hi_Lo,
  TXTCLI_Pot_Limit_HoldXem,
  TXTCLI_Pot_Limit_Omaha,
  TXTCLI_Razz,
  TXTCLI_Regular,
  TXTCLI_Showtime_HoldXem,
  TXTCLI_Showtime_Omaha,
  TXTCLI_Slow,
  TXTCLI_Speed,
  TXTCLI_Split_HoldXem,
  TXTCLI_Split_Omaha,
  TXTCLI_Stud,
  TXTCLI_Stud_Games,
  TXTCLI_Swap_HoldXem,
  TXTCLI_Table_Size,
  TXTHHY_6_Card_Omaha,
  TXTHHY_Deep_Water_HoldXem,
  TXTHHY_Tempest_HoldXem,
  TXTHHY_X040Turbo,
  TXTSRV_Freeroll
} from './filters.messages.json';

const FILTER_OPTION_LABEL_MAP: FilterOptionLabelMap = {
  [FilterName.Games]: {
    [FilterOptionGame.HoldemNL]: TXTCLI_No_Limit_HoldXem,
    [FilterOptionGame.HoldemPL]: TXTCLI_Pot_Limit_HoldXem,
    [FilterOptionGame.HoldemFL]: TXTCLI_Limit_HoldXem,
    [FilterOptionGame.HoldemSixPlus]: TXTCLI_6X_HoldXem,
    [FilterOptionGame.HoldemTempest]: TXTHHY_Tempest_HoldXem,
    [FilterOptionGame.HoldemShowTime]: TXTCLI_Showtime_HoldXem,
    [FilterOptionGame.HoldemSwap]: TXTCLI_Swap_HoldXem,
    [FilterOptionGame.HoldemSplit]: TXTCLI_Split_HoldXem,
    [FilterOptionGame.HoldemDeepWater]: TXTHHY_Deep_Water_HoldXem,
    [FilterOptionGame.OmahaNL]: TXTCLI_No_Limit_Omaha,
    [FilterOptionGame.OmahaPL]: TXTCLI_Pot_Limit_Omaha,
    [FilterOptionGame.Omaha5]: TXTCLI_5_Card_Omaha,
    [FilterOptionGame.Omaha6]: TXTHHY_6_Card_Omaha,
    [FilterOptionGame.OmahaHiLo]: TXTCLI_Omaha_Hi_Lo,
    [FilterOptionGame.OmahaShowTime]: TXTCLI_Showtime_Omaha,
    [FilterOptionGame.OmahaSplit]: TXTCLI_Split_Omaha,
    [FilterOptionGame.OmahaCourchevel]: TXTCLI_Courchevel,
    [FilterOptionGame.OmahaFusion]: TXTCLI_Fusion,
    [FilterOptionGame.Stud]: TXTCLI_Stud,
    [FilterOptionGame.Razz]: TXTCLI_Razz,
    [FilterOptionGame.Draw]: TXTCLI_5_Card_Draw,
    [FilterOptionGame.TripleDraw27]: TXTCLI_2_7_Triple_Draw,
    [FilterOptionGame.SingleDraw27]: TXTCLI_2_7_Single_Draw,
    [FilterOptionGame.Badugi]: TXTCLI_Badugi,
    [FilterOptionGame.Mixed8Game]: TXTCLI_8_Game,
    [FilterOptionGame.MixedHoldem]: TXTCLI_Mixed_HoldXem,
    [FilterOptionGame.MixedHoldemNLOmahaPL]: TXTCLI_HoldXemXOmaha,
    [FilterOptionGame.MixedHorse]: TXTCLI_HORSE,
    [FilterOptionGame.MixedHose]: TXTCLI_HOSE,
    [FilterOptionGame.MixedOmaha]: TXTCLI_Mixed_Omaha
  },
  [FilterName.BuyIn]: {
    [FilterOptionBuyIn.Freeroll]: TXTSRV_Freeroll,
    [FilterOptionBuyIn.Micro]: TXTCLI_Micro,
    [FilterOptionBuyIn.Low]: TXTCLI_Low,
    [FilterOptionBuyIn.Medium]: TXTCLI_Medium,
    [FilterOptionBuyIn.High]: TXTCLI_High,
    [FilterOptionBuyIn.Custom]: TXTCLI_Custom_Range
  },
  [FilterName.TableSize]: {
    [FilterOptionTableSize.Short]: TXTCLI_3_6_Players,
    [FilterOptionTableSize.Normal]: TXTCLI_7_10_Players,
    [FilterOptionTableSize.HeadsUp]: TXTCLI_2_Players
  },
  [FilterName.Speed]: {
    [FilterOptionSpeed.Slow]: TXTCLI_Slow,
    [FilterOptionSpeed.Normal]: TXTCLI_Regular,
    [FilterOptionSpeed.Turbo]: TXTHHY_X040Turbo,
    [FilterOptionSpeed.Hyper]: TXTCLI_Hyper_Turbo
  }
};

const FILTER_TITLE_MAP: { [K in FilterName]: TrackedTranslation } = {
  [FilterName.Games]: TXTCLI_Game,
  [FilterName.BuyIn]: TXTCLI_Buy_In,
  [FilterName.TableSize]: TXTCLI_Table_Size,
  [FilterName.Speed]: TXTCLI_Speed
};

const FILTER_ORDER: FilterName[] = [
  FilterName.Games,
  FilterName.BuyIn,
  FilterName.TableSize,
  FilterName.Speed
];

const FILTER_GROUP_TITLE_MAP: FilterOptionGroupLabelMap = {
  [FilterName.Games]: {
    [FilterSectionNameGame.Holdem]: TXTCLI_HoldXem_Games,
    [FilterSectionNameGame.Omaha]: TXTCLI_Omaha_Games,
    [FilterSectionNameGame.Stud]: TXTCLI_Stud_Games,
    [FilterSectionNameGame.Draw]: TXTCLI_Draw_Games,
    [FilterSectionNameGame.Mixed]: TXTCLI_Mixed_Games
  }
};

const mapFilterGroups = (filterName: FilterNameType, groups: RawSection[] = []) => {
  const mappedGroups: FilterOptionGroup[] = [];

  groups.forEach((group) => {
    const mappedOptions = mapFilterOptions(filterName, group.options);

    if (!mappedOptions?.length) return;

    mappedGroups.push({
      id: `${filterName}-${group.name}`,
      label: FILTER_GROUP_TITLE_MAP[filterName][group.name],
      options: mappedOptions
    });
  });

  return mappedGroups;
};

const mapFilterOptions = (filterName: FilterNameType, options: string[] = []) => {
  const mappedOptions: FilterOptionBase[] = [];

  options.forEach((option) => {
    const label = FILTER_OPTION_LABEL_MAP[filterName][option];
    if (label)
      mappedOptions.push({
        label,
        value: option,
        id: `${filterName}-${option.replaceAll('.', '-')}`
      });
  });

  return mappedOptions;
};

const mapFilter = (filter: RawFilter): Filter => {
  const name = filter.name as FilterNameType;
  const title = FILTER_TITLE_MAP[filter.name];
  const hasGroups = !!filter?.sections;

  return {
    name,
    title,
    hasGroups,
    options: hasGroups
      ? mapFilterGroups(name, filter.sections)
      : mapFilterOptions(name, filter.options)
  };
};

const filterNameComparator = (filterA: Filter, filterB: Filter) =>
  FILTER_ORDER.indexOf(filterA.name) - FILTER_ORDER.indexOf(filterB.name);

const getFilterData = (filterConfig: FilterTypes): Filter[] => {
  const {
    tourney: { main, more }
  } = filterConfig;

  return [...main, ...more]
    .filter((filter) => !!FILTER_TITLE_MAP[filter.name])
    .map((filter) => mapFilter(filter))
    .sort(filterNameComparator);
};

export const normalizeFiltersData = (filtersResponse: FiltersConfig[], site: string) => {
  const filterConfig = filtersResponse.find((filtersConfig) => {
    const parsedCriteria = parseHSPCriteria(filtersConfig.criteria);
    return criteriaMatches(parsedCriteria, { site: [site.toLowerCase()], platform: ['web'] });
  });

  if (!filterConfig)
    return {
      RM: [],
      PM: []
    };

  return {
    RM: getFilterData(filterConfig.rm),
    PM: getFilterData(filterConfig.pm)
  };
};

const convertKeysToCamelCase = (object: object) =>
  Object.keys(object).reduce((acc, key) => {
    acc[camelCase(key)] = object[key];
    return acc;
  }, {});

export const normalizeConfigQueryVariables = (
  configQueryVariables?: PokerMttQueryVariables
): TournamentsQueryVariables =>
  configQueryVariables
    ? {
        include: convertKeysToCamelCase(configQueryVariables.include),
        exclude: convertKeysToCamelCase(configQueryVariables.exclude)
      }
    : {};
